import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';
import {
  Plus, Trash2, QrCode, X, Edit, CheckCircle, XCircle, AlertTriangle,
} from '../components/Icons';
import { calcAge, calcIMT, estimateNutrition, nutritionLabel } from '../utils/childCalc';
import logo from '../assets/logo.png';
import styles from './ChildProfile.module.css';

function initial(name = '') {
  return (name.trim()[0] || '?').toUpperCase();
}

const EMPTY_FORM = {
  name: '', birth_date: '', gender: '', diagnosis: 'ASD',
  weight_kg: '', height_cm: '', lila_cm: '', imt_u_keterangan: '',
  comorbid_condition: false, medication_use: false,
  moderate_severe_autism: false, limited_verbal: false,
  low_social_interaction: false, restricted_diet: false,
};

function ChildProfile() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isID = i18n.language === 'id';

  const [children, setChildren] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = tambah, id = edit
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [qrChild, setQrChild] = useState(null);
  const qrCardRef = useRef(null);

  const fetchChildren = useCallback(async () => {
    const { data } = await supabase.from('children').select('*')
      .eq('caregiver_id', user.id).order('created_at', { ascending: false });
    if (data) setChildren(data);
  }, [user.id]);

  useEffect(() => { fetchChildren(); }, [fetchChildren]);

  // hasil auto-hitung (dihitung ulang tiap form berubah)
  const age = calcAge(form.birth_date, isID);
  const imt = calcIMT(form.weight_kg, form.height_cm);
  const nutrition = estimateNutrition(imt);

  const toneCls = (tone) =>
    tone === 'danger' ? styles.toneDanger : tone === 'warn' ? styles.toneWarn : styles.toneGood;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setForm(prev => ({ ...prev, [name]: checked }));
      return;
    }
    // untuk berat/tinggi/LILA: ubah koma desimal jadi titik (105,8 → 105.8)
    const numericFields = ['weight_kg', 'height_cm', 'lila_cm'];
    const cleaned = numericFields.includes(name) ? value.replace(',', '.') : value;
    setForm(prev => ({ ...prev, [name]: cleaned }));
  };

  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setErrorMsg('');
    setShowForm(true);
  };

  const openEdit = (child) => {
    setEditingId(child.id);
    setForm({
      name: child.name || '',
      birth_date: child.birth_date || '',
      gender: child.gender || '',
      diagnosis: child.diagnosis || 'ASD',
      weight_kg: child.weight_kg ?? '',
      height_cm: child.height_cm ?? '',
      lila_cm: child.lila_cm ?? '',
      imt_u_keterangan: child.imt_u_keterangan || '',
      comorbid_condition: !!child.comorbid_condition,
      medication_use: !!child.medication_use,
      moderate_severe_autism: !!child.moderate_severe_autism,
      limited_verbal: !!child.limited_verbal,
      low_social_interaction: !!child.low_social_interaction,
      restricted_diet: !!child.restricted_diet,
    });
    setErrorMsg('');
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setErrorMsg('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrorMsg('');
    setSuccessMsg('');

    // susun payload + isi kolom auto-hitung
    const payload = {
      caregiver_id: user.id,
      name: form.name,
      birth_date: form.birth_date || null,
      gender: form.gender,
      diagnosis: form.diagnosis,
      weight_kg: form.weight_kg ? parseFloat(form.weight_kg) : null,
      height_cm: form.height_cm ? parseFloat(form.height_cm) : null,
      lila_cm: form.lila_cm ? parseFloat(form.lila_cm) : null,
      // auto
      age: age.years,
      age_months: age.months,
      age_display: age.display,
      imt_u: imt != null ? String(imt) : null,
      imt_u_keterangan: form.imt_u_keterangan || (nutrition ? nutrition.dbValue : null),
      comorbid_condition: form.comorbid_condition,
      medication_use: form.medication_use,
      moderate_severe_autism: form.moderate_severe_autism,
      limited_verbal: form.limited_verbal,
      low_social_interaction: form.low_social_interaction,
      restricted_diet: form.restricted_diet,
    };

    let error;
    if (editingId) {
      ({ error } = await supabase.from('children').update(payload).eq('id', editingId));
    } else {
      ({ error } = await supabase.from('children').insert([payload]));
    }

    setSaving(false);

    if (error) {
      setErrorMsg(isID
        ? `Gagal menyimpan: ${error.message}. Data masih ada di formulir, silakan coba lagi.`
        : `Failed to save: ${error.message}. Your data is still in the form, please try again.`);
      return;
    }

    setSuccessMsg(editingId
      ? (isID ? 'Data anak berhasil diperbarui!' : 'Child data updated!')
      : (isID ? 'Data anak berhasil disimpan!' : 'Child data saved!'));
    closeForm();
    fetchChildren();
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  const handleDelete = async (id) => {
    const ok = window.confirm(isID
      ? 'Yakin ingin menghapus data anak ini? Semua catatan GI & skrining terkait juga akan terhapus.'
      : 'Delete this child? All related GI logs & screenings will also be deleted.');
    if (!ok) return;

    await supabase.from('gi_logs').delete().eq('child_id', id);
    await supabase.from('screenings').delete().eq('child_id', id);
    const { error } = await supabase.from('children').delete().eq('id', id);

    if (error) {
      setErrorMsg(isID ? `Gagal menghapus: ${error.message}` : `Failed to delete: ${error.message}`);
      return;
    }
    setSuccessMsg(isID ? 'Data anak berhasil dihapus.' : 'Child deleted.');
    fetchChildren();
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  const getChildUrl = (childId) => `${window.location.origin}/child-summary/${childId}`;

  const downloadQR = async (child) => {
    if (!qrCardRef.current) return;
    try {
      const canvas = await html2canvas(qrCardRef.current, {
        backgroundColor: '#ffffff', scale: 3, useCORS: true, allowTaint: true,
      });
      const link = document.createElement('a');
      link.download = `NOWGUT_QR_${child.name.replace(/\s+/g, '_')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      setErrorMsg(isID ? 'Unduh QR gagal. Coba lagi.' : 'QR download failed. Try again.');
    }
  };

  const riskFactors = [
    { key: 'comorbid_condition', label: t('child.comorbid') },
    { key: 'medication_use', label: t('child.medication') },
    { key: 'moderate_severe_autism', label: t('child.moderateSevere') },
    { key: 'limited_verbal', label: t('child.limitedVerbal') },
    { key: 'low_social_interaction', label: t('child.lowSocial') },
    { key: 'restricted_diet', label: t('child.restrictedDiet') },
  ];

  const avatarClass = (gender) =>
    gender === 'Perempuan' ? styles.childAvatarGirl
    : gender === 'Laki-laki' ? styles.childAvatarBoy
    : '';

  const riskTagsFor = (child) => [
    child.comorbid_condition && (isID ? 'Komorbid' : 'Comorbid'),
    child.medication_use && (isID ? 'Obat-obatan' : 'Medication'),
    child.moderate_severe_autism && (isID ? 'ASD Sedang-Berat' : 'Moderate-Severe ASD'),
    child.limited_verbal && (isID ? 'Verbal Terbatas' : 'Limited Verbal'),
    child.low_social_interaction && (isID ? 'Sosial Rendah' : 'Low Social'),
    child.restricted_diet && (isID ? 'Diet Ketat' : 'Restricted Diet'),
  ].filter(Boolean);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>{t('child.title')}</h1>
          <p className={styles.subtitle}>{t('child.subtitle')}</p>
        </div>
        <button className={styles.addBtn} onClick={showForm ? closeForm : openAdd}>
          {showForm
            ? <><X size={15} aria-hidden="true" /> {t('common.cancel')}</>
            : <><Plus size={15} aria-hidden="true" /> {t('child.addChild')}</>}
        </button>
      </div>

      {successMsg && (
        <div className={styles.successMsg}><CheckCircle size={16} aria-hidden="true" />{successMsg}</div>
      )}
      {errorMsg && (
        <div className={styles.errorMsg}><AlertTriangle size={18} aria-hidden="true" />{errorMsg}</div>
      )}

      {/* ===== FORM TAMBAH / EDIT ===== */}
      {showForm && (
        <div className={styles.formCard}>
          <h2 className={styles.formTitle}>
            {editingId
              ? (isID ? 'Edit Data Anak' : 'Edit Child Data')
              : t('child.addChild')}
          </h2>
          <form onSubmit={handleSubmit}>
            <div className={styles.sectionLabel}>{t('child.identity')}</div>
            <div className={styles.grid2}>
              <div className={styles.field}>
                <label className={styles.label}>{t('child.name')}</label>
                <input className={styles.input} name="name" value={form.name} onChange={handleChange} required />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>{isID ? 'Tanggal Lahir' : 'Birth Date'}</label>
                <input className={styles.input} type="date" name="birth_date"
                  value={form.birth_date} onChange={handleChange} required />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>{t('child.gender')}</label>
                <select className={styles.select} name="gender" value={form.gender} onChange={handleChange} required>
                  <option value="">-- Pilih --</option>
                  <option value="Laki-laki">{isID ? 'Laki-laki' : 'Male'}</option>
                  <option value="Perempuan">{isID ? 'Perempuan' : 'Female'}</option>
                </select>
              </div>
              <div className={styles.field}>
                <label className={styles.label}>{isID ? 'Diagnosis' : 'Diagnosis'}</label>
                <input className={styles.input} name="diagnosis" value={form.diagnosis} onChange={handleChange} />
              </div>
            </div>

            <div className={styles.sectionLabel}>{isID ? 'Antropometri' : 'Anthropometry'}</div>
            <div className={styles.grid2}>
              <div className={styles.field}>
                <label className={styles.label}>{t('child.weight')} (kg)</label>
                <input className={styles.input} type="number" name="weight_kg"
                  value={form.weight_kg} onChange={handleChange} step="0.1" min="0" required />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>{t('child.height')} (cm)</label>
                <input className={styles.input} type="number" name="height_cm"
                  value={form.height_cm} onChange={handleChange} step="0.1" min="0" required />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>LILA (cm) <span style={{ fontWeight: 400, color: 'var(--body-mid)' }}>· {isID ? 'opsional' : 'optional'}</span></label>
                <input className={styles.input} type="number" name="lila_cm"
                  value={form.lila_cm} onChange={handleChange} step="0.1" min="0"
                  placeholder={isID ? 'Lingkar lengan atas' : 'Mid-upper arm'} />
              </div>
            </div>

            {/* HASIL AUTO-HITUNG */}
            <div className={styles.autoBox}>
              <div className={styles.autoRow}>
                <span className={styles.autoLabel}>{isID ? 'Usia (otomatis)' : 'Age (auto)'}</span>
                <span className={styles.autoValue}>{age.display}</span>
              </div>
              <div className={styles.autoRow}>
                <span className={styles.autoLabel}>{isID ? 'IMT (otomatis)' : 'BMI (auto)'}</span>
                <span className={styles.autoValue}>{imt != null ? imt : '-'}</span>
              </div>
              {nutrition && (
                <div className={styles.autoRow}>
                  <span className={styles.autoLabel}>{isID ? 'Perkiraan status gizi' : 'Estimated nutrition'}</span>
                  <span className={`${styles.autoNote} ${toneCls(nutrition.tone)}`}>
                    {nutritionLabel(nutrition, isID)}
                  </span>
                </div>
              )}
              <div className={styles.disclaimer}>
                <AlertTriangle size={14} aria-hidden="true" />
                {isID
                  ? 'Perkiraan kasar, BUKAN diagnosis. Status gizi resmi (IMT/U) mengikuti kurva pertumbuhan WHO sesuai usia & jenis kelamin — konsultasikan dengan tenaga gizi/dokter.'
                  : 'Rough estimate, NOT a diagnosis. Official nutrition status (BMI-for-age) follows WHO growth curves by age & sex — consult a nutritionist/doctor.'}
              </div>
            </div>

            <div className={styles.sectionLabel}>{t('child.riskFactors')}</div>
            <div className={styles.riskGrid}>
              {riskFactors.map(r => (
                <div key={r.key} className={styles.riskItem}>
                  <span className={styles.riskItemLabel}>{r.label}</span>
                  <div className={styles.toggleGroup}>
                    <label className={styles.radioLabel}>
                      <input type="radio" name={r.key} checked={form[r.key] === true}
                        onChange={() => setForm(p => ({ ...p, [r.key]: true }))} />
                      {isID ? 'Ya' : 'Yes'}
                    </label>
                    <label className={styles.radioLabel}>
                      <input type="radio" name={r.key} checked={form[r.key] === false}
                        onChange={() => setForm(p => ({ ...p, [r.key]: false }))} />
                      {isID ? 'Tidak' : 'No'}
                    </label>
                  </div>
                </div>
              ))}
            </div>

            <button type="submit" className={styles.submitBtn} disabled={saving}>
              {saving
                ? t('common.loading')
                : <><CheckCircle size={18} aria-hidden="true" />
                    {editingId ? (isID ? 'Simpan Perubahan' : 'Save Changes') : t('common.save')}</>}
            </button>
            {editingId && (
              <button type="button" className={styles.cancelEditBtn} onClick={closeForm}>
                {isID ? 'Batal edit' : 'Cancel edit'}
              </button>
            )}
          </form>
        </div>
      )}

      {/* ===== DAFTAR ANAK ===== */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('child.list')}</h2>
        {children.length === 0 ? (
          <p className={styles.empty}>{t('child.noChild')}</p>
        ) : (
          children.map(child => {
            const tags = riskTagsFor(child);
            return (
              <div key={child.id} className={styles.childCard}>
                <div className={styles.childHeader}>
                  <div className={styles.childIdentity}>
                    <span className={`${styles.childAvatar} ${avatarClass(child.gender)}`}>
                      {initial(child.name)}
                    </span>
                    <div>
                      <div className={styles.childName}>{child.name}</div>
                      <div className={styles.childMeta}>
                        {child.age_display || (child.age ? `${child.age} ${isID ? 'tahun' : 'yrs'}` : '-')}
                        {child.gender ? ` • ${child.gender}` : ''}
                      </div>
                    </div>
                  </div>
                  <div className={styles.childActions}>
                    <button className={styles.iconBtn} onClick={() => openEdit(child)}>
                      <Edit size={13} aria-hidden="true" />{isID ? 'Edit' : 'Edit'}
                    </button>
                    <button className={`${styles.iconBtn} ${styles.qrBtn}`}
                      onClick={() => setQrChild(qrChild?.id === child.id ? null : child)}>
                      {qrChild?.id === child.id
                        ? <><X size={13} aria-hidden="true" /> {isID ? 'Tutup' : 'Close'}</>
                        : <><QrCode size={13} aria-hidden="true" /> QR</>}
                    </button>
                    <button className={`${styles.iconBtn} ${styles.deleteBtn}`} onClick={() => handleDelete(child.id)}>
                      <Trash2 size={13} aria-hidden="true" />
                    </button>
                  </div>
                </div>

                <div className={styles.childStats}>
                  <div className={styles.stat}>
                    <div className={styles.statLabel}>{isID ? 'Berat' : 'Weight'}</div>
                    <div className={styles.statValue}>{child.weight_kg ?? '-'} kg</div>
                  </div>
                  <div className={styles.stat}>
                    <div className={styles.statLabel}>{isID ? 'Tinggi' : 'Height'}</div>
                    <div className={styles.statValue}>{child.height_cm ?? '-'} cm</div>
                  </div>
                  <div className={styles.stat}>
                    <div className={styles.statLabel}>IMT</div>
                    <div className={styles.statValue}>{child.imt_u || '-'}</div>
                    {child.imt_u_keterangan && (
                      <div className={`${styles.statNote} ${toneCls(
                        child.imt_u_keterangan.includes('Obesitas') ? 'danger'
                        : child.imt_u_keterangan.includes('Lebih') || child.imt_u_keterangan.includes('Kurang') ? 'warn'
                        : 'good')}`}>
                        {child.imt_u_keterangan}
                      </div>
                    )}
                  </div>
                  <div className={styles.stat}>
                    <div className={styles.statLabel}>{isID ? 'Diagnosis' : 'Diagnosis'}</div>
                    <div className={styles.statValue}>{child.diagnosis || '-'}</div>
                  </div>
                </div>

                <div className={styles.riskList}>
                  {riskFactors.map(r => (
                    <div key={r.key} className={styles.riskRow}>
                      <span className={styles.riskRowLabel}>{r.label}</span>
                      {child[r.key]
                        ? <span className={styles.riskYes}><CheckCircle size={14} aria-hidden="true" />{isID ? 'Ya' : 'Yes'}</span>
                        : <span className={styles.riskNo}><XCircle size={14} aria-hidden="true" />{isID ? 'Tidak' : 'No'}</span>}
                    </div>
                  ))}
                </div>

                {/* QR PANEL */}
                {qrChild?.id === child.id && (
                  <div className={styles.qrPanel}>
                    <div className={styles.qrPanelTitle}>
                      <QrCode size={16} aria-hidden="true" />
                      {isID ? 'QR Ringkasan Medis' : 'Medical Summary QR'}
                    </div>
                    <p className={styles.qrPanelDesc}>
                      {isID
                        ? `Tunjukkan atau cetak QR ini untuk dokter/perawat. Pindai untuk melihat data lengkap ${child.name}.`
                        : `Show or print this QR for the doctor/nurse. Scan to view ${child.name}'s full data.`}
                    </p>

                    <div className={styles.qrCardWrapper}>
                      <div ref={qrCardRef} className={styles.qrCard}>
                        <div className={styles.qrCardHeader}>
                          <div className={styles.qrLogoRow}>
                            <img src={logo} alt="NOWGUT" className={styles.qrLogoImg} />
                            <div className={styles.qrCardLogo}>NOWGUT</div>
                          </div>
                          <div className={styles.qrCardTagline}>
                            {isID ? 'Pemantau Risiko Saluran Cerna Anak' : 'Child GI Risk Monitor'}
                          </div>
                        </div>
                        <div className={styles.qrCardBody}>
                          <div className={styles.qrCardLeft}>
                            <div className={styles.qrCardName}>{child.name}</div>
                            <div className={styles.qrCardInfo}>{child.gender} • {child.age_display || `${child.age} ${isID ? 'th' : 'yrs'}`}</div>
                            <div className={styles.qrCardInfo}>{child.weight_kg} kg • {child.height_cm} cm</div>
                            <div className={styles.qrCardInfo}>{child.diagnosis}</div>
                            {tags.length > 0 && (
                              <div className={styles.qrCardRisks}>
                                <div className={styles.qrCardRiskTitle}>{isID ? 'Faktor Risiko:' : 'Risk Factors:'}</div>
                                <div className={styles.qrCardRiskTags}>
                                  {tags.map(r => <span key={r} className={styles.qrCardRiskTag}>{r}</span>)}
                                </div>
                              </div>
                            )}
                          </div>
                          <div className={styles.qrCardRight}>
                            <QRCodeSVG value={getChildUrl(child.id)} size={130}
                              fgColor="#0a5252" bgColor="#FFFFFF" level="M" />
                          </div>
                        </div>
                        <div className={styles.qrCardFooter}>
                          <div className={styles.qrCardFooterText}>
                            {isID ? 'Pindai untuk data lengkap' : 'Scan for full data'}
                          </div>
                          <div className={styles.qrCardFooterUrl}>nowgut.com</div>
                        </div>
                      </div>
                    </div>

                    <button className={styles.qrDownloadBtn} onClick={() => downloadQR(child)}>
                      <QrCode size={16} aria-hidden="true" />{isID ? 'Unduh Kartu' : 'Download Card'}
                    </button>

                    <div className={styles.qrUrlBox}>
                      <span className={styles.qrUrlLabel}>{isID ? 'Tautan langsung:' : 'Direct link:'}</span>
                      <a className={styles.qrUrl} href={getChildUrl(child.id)} target="_blank" rel="noreferrer">
                        {getChildUrl(child.id)}
                      </a>
                    </div>

                    <div className={styles.qrWarn}>
                      <AlertTriangle size={14} aria-hidden="true" />
                      {isID
                        ? 'Siapa pun yang memegang QR ini dapat melihat data anak. Gunakan hanya saat konsultasi medis.'
                        : 'Anyone with this QR can view the child\u2019s data. Use only during medical consultations.'}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default ChildProfile;